import React, { useState, useId } from "react";
import {
  useUploadDocument,
  useCheckDocument,
  useDeleteDocument,
} from "http-hooks/document/useDocument";
import { useQueryClient } from "@tanstack/react-query";

const FileUploader = ({
  docFilename = "",
  onUploadSuccess,
  onDeleteSuccess,
  acceptedTypes = "image/png,image/jpg,image/jpeg,application/pdf",
  description = "PNG, JPG, PDF up to 10MB",
  label = "Upload File",
  className = "",
  deferred = false,
  selectedFile = null,
  onFileSelected,
}) => {
  const uniqueId = useId();
  const inputId = `file-upload-${uniqueId}`;
  const [dragActive, setDragActive] = useState(false);
  const queryClient = useQueryClient();

  const { data: checkData, isLoading: isChecking } = useCheckDocument(docFilename);
  const { mutateAsync: uploadDoc, isPending: isUploading } = useUploadDocument();
  const { mutateAsync: deleteDoc, isPending: isDeleting } = useDeleteDocument();

  const existingFile = checkData?.exists ? checkData : null;

  const handleFile = async (file) => {
    if (!file) return;
    if (deferred) {
      onFileSelected?.(file);
      return;
    }
    if (!docFilename) return;
    try {
      const result = await uploadDoc({ file, filename: docFilename });
      queryClient.invalidateQueries({ queryKey: ["doc-check", docFilename] });
      if (onUploadSuccess) onUploadSuccess(result);
    } catch (error) {
      console.error("Upload failed:", error);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) handleFile(file);
    e.target.value = "";
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleDelete = async () => {
    if (!docFilename) return;
    if (!window.confirm("Are you sure you want to delete this file?")) return;
    try {
      await deleteDoc(docFilename);
      queryClient.invalidateQueries({ queryKey: ["doc-check", docFilename] });
      if (onDeleteSuccess) onDeleteSuccess();
    } catch (error) {
      console.error("Delete failed:", error);
    }
  };

  const isProcessing = isUploading || isDeleting;

  if (!docFilename && !deferred) return null;

  return (
    <div className={className}>
      <label className="block text-sm font-medium leading-6 text-gray-900">
        {label}
      </label>

      {isChecking ? (
        <div className="mt-2 flex justify-center rounded-lg border border-dashed border-gray-300 px-6 py-4">
          <div className="flex items-center gap-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
            <span className="text-sm text-gray-500">Checking...</span>
          </div>
        </div>
      ) : deferred && selectedFile ? (
        <div className="mt-2 flex items-center justify-between rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
          <div className="text-sm text-blue-800">
            <span className="font-medium">{selectedFile.name}</span>
            <span className="ml-2 text-blue-600">Will upload after the voucher is saved.</span>
          </div>
          <button
            type="button"
            onClick={() => onFileSelected?.(null)}
            className="text-sm font-semibold text-red-600 hover:text-red-800"
          >
            Remove
          </button>
        </div>
      ) : existingFile ? (
        <div className="mt-2">
          <div className="flex items-center justify-between rounded-lg border border-green-200 bg-green-50 px-4 py-3">
            <div className="flex items-center gap-2">
              <svg className="h-5 w-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
              <a
                href={`${process.env.REACT_APP_MAIN_BASE_URL}/${existingFile.filePath}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-green-700 hover:text-green-900 underline"
              >
                {existingFile.fileName}
              </a>
            </div>
            <button
              type="button"
              onClick={handleDelete}
              disabled={isProcessing}
              className="text-red-500 hover:text-red-700 disabled:opacity-50 p-1"
              title="Delete file"
            >
              <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
                <path
                  fillRule="evenodd"
                  d="M8.75 1A2.75 2.75 0 006 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 10.23 1.482l.149-.022.841 10.518A2.75 2.75 0 007.596 19h4.807a2.75 2.75 0 002.742-2.53l.841-10.52.149.023a.75.75 0 00.23-1.482A41.03 41.03 0 0014 4.193V3.75A2.75 2.75 0 0011.25 1h-2.5zM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4zM8.58 7.72a.75.75 0 00-1.5.06l.3 7.5a.75.75 0 101.5-.06l-.3-7.5zm4.34.06a.75.75 0 10-1.5-.06l-.3 7.5a.75.75 0 101.5.06l.3-7.5z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        </div>
      ) : (
        <div
          className={`mt-2 flex justify-center rounded-lg border border-dashed px-6 py-4 ${
            dragActive
              ? "border-indigo-600 bg-indigo-50"
              : "border-gray-900/25"
          } ${isProcessing ? "opacity-50 pointer-events-none" : ""}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <div className="text-center">
            {isUploading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mr-2"></div>
                <span className="text-sm text-gray-600">Uploading...</span>
              </div>
            ) : (
              <>
                <svg
                  className="mx-auto h-10 w-10 text-gray-300"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M11.47 2.47a.75.75 0 011.06 0l3.75 3.75a.75.75 0 01-1.06 1.06l-2.47-2.47V15a.75.75 0 01-1.5 0V4.81L8.78 7.28a.75.75 0 01-1.06-1.06l3.75-3.75zM3 15.75a.75.75 0 01.75.75v2.25a1.5 1.5 0 001.5 1.5h13.5a1.5 1.5 0 001.5-1.5V16.5a.75.75 0 011.5 0v2.25a3 3 0 01-3 3H5.25a3 3 0 01-3-3V16.5a.75.75 0 01.75-.75z"
                    clipRule="evenodd"
                  />
                </svg>
                <div className="mt-2 flex text-sm leading-6 text-gray-600">
                  <label
                    htmlFor={inputId}
                    className="relative cursor-pointer rounded-md bg-white font-semibold text-indigo-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-indigo-600 focus-within:ring-offset-2 hover:text-indigo-500"
                  >
                    <span>Upload a file</span>
                    <input
                      id={inputId}
                      name={inputId}
                      type="file"
                      className="sr-only"
                      onChange={handleFileChange}
                      accept={acceptedTypes}
                    />
                  </label>
                  <p className="pl-1">or drag and drop</p>
                </div>
                <p className="text-xs leading-5 text-gray-600">{description}</p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUploader;
